import type { FindingsPeriod } from "@iamu/shared";
import { type ReactNode, useId } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { formatDay, isoDay, QUICK_PERIODS } from "./scope-url-state";

export interface BuildOption {
  id: string;
  label: string;
  gitSha?: string | null;
  gitBranch: string | null;
  environment?: string | null;
  firstRunAt: string | Date | null;
  createdAt: string | Date;
}

const ISO_DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

export function formatBuildDisplayLabel(b: {
  label: string;
  gitSha?: string | null;
  environment?: string | null;
}): { primary: string; sha: string | null } {
  const shortSha = b.gitSha ? b.gitSha.slice(0, 7) : null;
  if (ISO_DAY_RE.test(b.label) && !shortSha) {
    const envPrefix = b.environment ? `${b.environment} ` : "build ";
    return { primary: `${envPrefix}${b.label}`, sha: null };
  }
  const showSha = shortSha && !b.label.includes(shortSha) ? shortSha : null;
  return { primary: b.label, sha: showSha };
}

export function PeriodOptions({
  period,
  builds,
  onChange,
}: {
  period: FindingsPeriod | undefined;
  builds: BuildOption[];
  onChange: (p: FindingsPeriod | undefined) => void;
}) {
  const id = useId();
  const today = isoDay(new Date());
  const range = period?.kind === "range" ? period : null;

  const option = (
    key: string,
    selected: boolean,
    onSelect: () => void,
    text: ReactNode,
    meta?: ReactNode,
  ) => (
    <li key={key}>
      <button
        type="button"
        aria-pressed={selected}
        onClick={onSelect}
        className={cn(
          "flex w-full items-center justify-between gap-3 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
          selected && "bg-muted font-medium",
        )}
      >
        <span className="min-w-0 truncate">{text}</span>
        {meta && (
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
            {meta}
          </span>
        )}
      </button>
    </li>
  );

  return (
    <div className="divide-y divide-border">
      <div className="p-3">
        <p className="mb-1 px-2 text-xs text-muted-foreground" id={`${id}-q`}>
          Quick
        </p>
        <ul aria-labelledby={`${id}-q`} className="space-y-0.5">
          {QUICK_PERIODS.map((days) =>
            option(
              `last-${days}`,
              period?.kind === "last" && period.days === days,
              () => onChange({ kind: "last", days }),
              `Last ${days} days`,
            ),
          )}
          {option("all", !period, () => onChange(undefined), "All time")}
        </ul>
      </div>

      {builds.length > 0 && (
        <div className="p-3">
          <p className="mb-1 px-2 text-xs text-muted-foreground" id={`${id}-b`}>
            Since a build
          </p>
          <ul aria-labelledby={`${id}-b`} className="space-y-0.5">
            {builds.map((b) => {
              const at = b.firstRunAt ?? b.createdAt;
              const { primary, sha } = formatBuildDisplayLabel(b);
              return option(
                b.id,
                period?.kind === "sinceBuild" && period.buildId === b.id,
                () => onChange({ kind: "sinceBuild", buildId: b.id }),
                <>
                  <span className="font-mono text-xs">{primary}</span>
                  {sha && (
                    <span className="font-mono text-xs text-muted-foreground">
                      {" "}
                      · {sha}
                    </span>
                  )}
                  {b.gitBranch && (
                    <span className="text-muted-foreground">
                      {" "}
                      · {b.gitBranch}
                    </span>
                  )}
                </>,
                <time dateTime={new Date(at).toISOString()}>
                  {formatDay(isoDay(new Date(at)))}
                </time>,
              );
            })}
          </ul>
        </div>
      )}

      <fieldset className="space-y-2 p-3">
        <legend className="px-2 text-xs text-muted-foreground">
          Custom range
        </legend>
        <div className="grid grid-cols-2 gap-2 px-2">
          <div className="space-y-1">
            <Label htmlFor={`${id}-since`} className="text-xs">
              From
            </Label>
            <Input
              id={`${id}-since`}
              type="date"
              max={range?.until ?? today}
              value={range?.since ?? ""}
              onChange={(e) => {
                const since = e.currentTarget.value;
                if (since) {
                  onChange({ kind: "range", since, until: range?.until });
                }
              }}
              className="h-9"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor={`${id}-until`} className="text-xs">
              To <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id={`${id}-until`}
              type="date"
              min={range?.since}
              max={today}
              disabled={!range}
              value={range?.until ?? ""}
              onChange={(e) => {
                if (!range) return;
                onChange({
                  kind: "range",
                  since: range.since,
                  until: e.currentTarget.value || undefined,
                });
              }}
              className="h-9"
            />
          </div>
        </div>
        <p className="px-2 text-xs text-muted-foreground">
          Leave “To” empty to include everything up to now.
        </p>
      </fieldset>
    </div>
  );
}
